import { LocaleEnum } from '@systemeio/ui-frontend'
import type { i18n as I18nInstance } from 'i18next'
import type { NextRouter } from 'next/router'
import type { KeyedMutator } from 'swr'
import { changeLanguage } from 'shared/api/user-api'
import type { UnauthorizedUser, UserInterface } from 'shared/types/user-interface'
import { isClient } from 'shared/utils/is-client'
import { resolveLocaleInPath } from 'shared/utils/resolve-locale-in-path'
import { updateRuntimeLocale } from './update-runtime-locale'

type LocaleValue = LocaleEnum | string
type UserMutator = KeyedMutator<UserInterface | UnauthorizedUser>
type RouterLike = Pick<NextRouter, 'asPath' | 'locale' | 'pathname' | 'push'>

interface ChangeLocaleOptions {
  locale?: LocaleValue
  i18n?: I18nInstance | null
  persistCookie?: boolean
  disableBackendUpdate?: boolean
  userDashboardLocale?: LocaleValue
  mutateUser?: UserMutator
  revalidateUser?: boolean
  router?: RouterLike
  forceRouterUpdate?: boolean
}

class LocaleChangeManager {
  private isChanging = false

  public get changeIsInProgres(): boolean {
    return this.isChanging
  }

  public async changeLocale({
    locale,
    i18n,
    persistCookie = true,
    disableBackendUpdate,
    userDashboardLocale,
    mutateUser,
    revalidateUser = true,
    router,
    forceRouterUpdate = false,
  }: ChangeLocaleOptions) {
    if (!locale) return { changed: false }
    if (this.isChanging) return { changed: false }

    const clientSide = isClient()
    const localeString = String(locale)
    if (i18n && i18n.language === localeString) return { changed: false }

    this.isChanging = true
    try {
      await this.updateBackendLocale(localeString, {
        disableBackendUpdate,
        userDashboardLocale,
        clientSide,
      })

      const [changed] = await Promise.all([
        updateRuntimeLocale({
          i18n,
          locale: localeString,
          persistCookie,
        }),
        this.updateUserLocaleState(localeString, {
          mutateUser,
          revalidateUser,
          clientSide,
        }),
      ])

      await this.updateRouterLocale(localeString, {
        router,
        clientSide,
        forceRouterUpdate,
      })

      return { changed }
    } finally {
      this.isChanging = false
    }
  }

  private async updateBackendLocale(
    localeString: string,
    options: {
      disableBackendUpdate?: boolean
      userDashboardLocale?: LocaleValue
      clientSide: boolean
    },
  ) {
    const { disableBackendUpdate, userDashboardLocale, clientSide } = options
    const canUpdateBackend = !disableBackendUpdate && clientSide

    if (!canUpdateBackend) return

    const shouldUpdateBackend = !userDashboardLocale || userDashboardLocale !== localeString

    if (shouldUpdateBackend) {
      await changeLanguage(localeString as LocaleEnum)
    }
  }

  private async updateUserLocaleState(
    localeString: string,
    options: {
      mutateUser?: UserMutator
      revalidateUser: boolean
      clientSide: boolean
    },
  ) {
    const { mutateUser, revalidateUser, clientSide } = options

    if (!clientSide || !mutateUser) return

    await mutateUser(currentUser => {
      if (!currentUser || typeof currentUser !== 'object') {
        return currentUser as unknown as UserInterface | UnauthorizedUser
      }

      if (!('dashboardLocale' in currentUser) || currentUser.dashboardLocale === localeString) {
        return currentUser as unknown as UserInterface | UnauthorizedUser
      }

      return {
        ...currentUser,
        dashboardLocale: localeString as LocaleEnum,
      } as UserInterface | UnauthorizedUser
    }, revalidateUser)
  }

  private async updateRouterLocale(
    localeString: string,
    options: {
      router?: RouterLike
      clientSide: boolean
      forceRouterUpdate?: boolean
    },
  ) {
    const { router, clientSide, forceRouterUpdate } = options

    if (!clientSide || !router) return

    const { localeInPath, hasLocaleInPath } = resolveLocaleInPath(window.location.pathname)

    if ((hasLocaleInPath || forceRouterUpdate) && localeInPath !== localeString) {
      try {
        await router.push(router.pathname, router.asPath, { locale: localeString })
      } catch (error) {
        throw new Error('Failed to update locale in URL', { cause: error })
      }
    }
  }
}

const localeChangeManager = new LocaleChangeManager()

export const changeLocale = (options: ChangeLocaleOptions) =>
  localeChangeManager.changeLocale(options)
